import { DESIGN_SYSTEM } from '../constants/designSystem';

const SKELETON_STYLE = {
  background: `linear-gradient(90deg, ${DESIGN_SYSTEM.colors.bg.elevated} 25%, ${DESIGN_SYSTEM.colors.bg.surface} 50%, ${DESIGN_SYSTEM.colors.bg.elevated} 75%)`,
  backgroundSize: '200% 100%',
  animation: 'skeletonShimmer 1.6s ease-in-out infinite',
  borderRadius: 8,
};

// Matches the Cream Pixel sidebar+content shell
export function AppShellSkeleton() {
  const S = DESIGN_SYSTEM.colors;
  const bar = (w, h, r = 6, extra = {}) => (
    <div style={{ ...SKELETON_STYLE, width: w, height: h, borderRadius: r, flexShrink: 0, ...extra }} />
  );

  return (
    <div style={{ display: 'flex', width: '100%', height: '100vh', background: S.bg.primary, overflow: 'hidden' }}>

      {/* ── Sidebar ─────────────────────────────── */}
      <div style={{ width: 240, minWidth: 240, background: S.bg.secondary, borderRight: `1px solid ${S.border.light}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>

        {/* Logo area */}
        <div style={{ padding: '22px 20px', borderBottom: `1px solid ${S.border.light}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          {bar(36, 36, 8)}
          {bar(90, 14, 6)}
        </div>

        {/* Nav items */}
        <div style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[80, 100, 70, 95, 75, 85].map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 8px', borderRadius: 8, background: i === 0 ? S.bg.surface : 'transparent' }}>
              {bar(18, 18, 5)}
              {bar(w, 12, 5)}
            </div>
          ))}
        </div>
      </div>

      {/* ── Main content ──────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: S.bg.primary }}>

        {/* Page header */}
        <div style={{ padding: '24px 28px 16px', borderBottom: `1px solid ${S.border.light}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {bar(160, 22, 6)}
          {bar(90, 32, 8)}
        </div>

        {/* Content rows */}
        <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ background: S.bg.card, border: `2px solid ${S.border.light}`, borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: `3px 3px 0 ${S.border.light}` }}>
              {bar(68, 68, 8)}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {bar(`${55 + (i * 7) % 20}%`, 14, 5)}
                <div style={{ display: 'flex', gap: 6 }}>
                  {bar(60, 20, 20)}
                  {bar(50, 20, 20)}
                  {bar(44, 20, 20)}
                </div>
              </div>
              {bar(80, 10, 5)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Lighter skeleton for Suspense fallbacks inside the main content area (sidebar already rendered)
export function PageSkeleton({ rows = 5 }) {
  const S = DESIGN_SYSTEM.colors;
  return (
    <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Page title */}
      <div style={{ ...SKELETON_STYLE, height: 22, width: 180, borderRadius: 6, marginBottom: 8 }} />
      {/* Content rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ background: S.bg.card, border: `2px solid ${S.border.light}`, borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: `3px 3px 0 ${S.border.light}` }}>
          <div style={{ ...SKELETON_STYLE, width: 68, height: 68, borderRadius: 8, flexShrink: 0 }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ ...SKELETON_STYLE, height: 14, width: `${50 + (i * 11) % 28}%`, borderRadius: 5 }} />
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ ...SKELETON_STYLE, height: 20, width: 62, borderRadius: 20 }} />
              <div style={{ ...SKELETON_STYLE, height: 20, width: 52, borderRadius: 20 }} />
            </div>
          </div>
          <div style={{ ...SKELETON_STYLE, height: 10, width: 80, borderRadius: 5 }} />
        </div>
      ))}
    </div>
  );
}

// Public catalog page skeleton — grid of song cards
export function CatalogPageSkeleton() {
  return (
    <div style={{ minHeight: '100vh', background: '#08080C', padding: '40px 24px' }}>
      {/* Header bar */}
      <div style={{ maxWidth: 1100, margin: '0 auto 32px' }}>
        <div style={{ ...SKELETON_STYLE, height: 28, width: 220, borderRadius: 6, marginBottom: 12, background: 'linear-gradient(90deg,#1c1c24 25%,#26262e 50%,#1c1c24 75%)', backgroundSize: '200% 100%' }} />
        <div style={{ ...SKELETON_STYLE, height: 14, width: 340, borderRadius: 5, background: 'linear-gradient(90deg,#1c1c24 25%,#26262e 50%,#1c1c24 75%)', backgroundSize: '200% 100%' }} />
      </div>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} style={{ background: '#1c1c24', border: '2px solid #2e2e38', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ ...SKELETON_STYLE, height: 120, background: 'linear-gradient(90deg,#222230 25%,#2a2a3a 50%,#222230 75%)', backgroundSize: '200% 100%', borderRadius: 0 }} />
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ ...SKELETON_STYLE, height: 13, width: '70%', background: 'linear-gradient(90deg,#222230 25%,#2a2a3a 50%,#222230 75%)', backgroundSize: '200% 100%' }} />
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ ...SKELETON_STYLE, height: 20, width: 55, borderRadius: 20, background: 'linear-gradient(90deg,#222230 25%,#2a2a3a 50%,#222230 75%)', backgroundSize: '200% 100%' }} />
                <div style={{ ...SKELETON_STYLE, height: 20, width: 48, borderRadius: 20, background: 'linear-gradient(90deg,#222230 25%,#2a2a3a 50%,#222230 75%)', backgroundSize: '200% 100%' }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Song card skeleton
export function SongCardSkeleton() {
  return (
    <div style={{ background: DESIGN_SYSTEM.colors.bg.card, border: `1px solid ${DESIGN_SYSTEM.colors.border.light}`, borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ ...SKELETON_STYLE, height: 120, borderRadius: 12 }} />
      <div style={{ ...SKELETON_STYLE, height: 16, width: '70%' }} />
      <div style={{ ...SKELETON_STYLE, height: 12, width: '45%' }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ ...SKELETON_STYLE, height: 24, width: 60, borderRadius: 20 }} />
        <div style={{ ...SKELETON_STYLE, height: 24, width: 50, borderRadius: 20 }} />
      </div>
    </div>
  );
}

// Opportunity card skeleton
export function OpportunityCardSkeleton() {
  return (
    <div style={{ background: DESIGN_SYSTEM.colors.bg.card, border: `1px solid ${DESIGN_SYSTEM.colors.border.light}`, borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ ...SKELETON_STYLE, height: 18, width: '55%' }} />
        <div style={{ ...SKELETON_STYLE, height: 24, width: 70, borderRadius: 20 }} />
      </div>
      <div style={{ ...SKELETON_STYLE, height: 13, width: '90%' }} />
      <div style={{ ...SKELETON_STYLE, height: 13, width: '75%' }} />
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <div style={{ ...SKELETON_STYLE, height: 22, width: 80, borderRadius: 20 }} />
        <div style={{ ...SKELETON_STYLE, height: 22, width: 65, borderRadius: 20 }} />
      </div>
    </div>
  );
}

// Message thread skeleton
export function MessageThreadSkeleton() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}>
      <div style={{ ...SKELETON_STYLE, width: 44, height: 44, borderRadius: '50%', flexShrink: 0 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ ...SKELETON_STYLE, height: 14, width: '60%' }} />
        <div style={{ ...SKELETON_STYLE, height: 12, width: '85%' }} />
      </div>
    </div>
  );
}

// Stat card skeleton
export function StatCardSkeleton() {
  return (
    <div style={{ background: DESIGN_SYSTEM.colors.bg.card, border: `1px solid ${DESIGN_SYSTEM.colors.border.light}`, borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ ...SKELETON_STYLE, height: 13, width: '50%' }} />
        <div style={{ ...SKELETON_STYLE, height: 32, width: 32, borderRadius: 8 }} />
      </div>
      <div style={{ ...SKELETON_STYLE, height: 32, width: '40%' }} />
    </div>
  );
}

// Generic line skeleton (reusable)
export function LineSkeleton({ width = '100%', height = 14, style = {} }) {
  return <div style={{ ...SKELETON_STYLE, height, width, borderRadius: 6, ...style }} />;
}

// Table row skeleton
export function TableRowSkeleton({ cols = 4 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16, padding: '16px 0', borderBottom: `1px solid ${DESIGN_SYSTEM.colors.border.light}` }}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} style={{ ...SKELETON_STYLE, height: 14, width: i === 0 ? '80%' : '60%' }} />
      ))}
    </div>
  );
}

// Page-level skeleton grid helpers
export function SongGridSkeleton({ count = 6 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20 }}>
      {Array.from({ length: count }).map((_, i) => <SongCardSkeleton key={i} />)}
    </div>
  );
}

export function OpportunityGridSkeleton({ count = 4 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {Array.from({ length: count }).map((_, i) => <OpportunityCardSkeleton key={i} />)}
    </div>
  );
}

export function MessageListSkeleton({ count = 6 }) {
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => <MessageThreadSkeleton key={i} />)}
    </div>
  );
}
